import { AfterViewInit, Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { AuthService } from "src/app/services/auth/auth.service";
import { GenerateErrors } from "src/app/utils/generate-errors";
import { Webflow } from "src/app/utils/webflow";
import Swal from "sweetalert2";

import intlTelInput from "intl-tel-input";
import {
  FacebookLoginProvider,
  GoogleLoginProvider,
  SocialAuthService,
} from "angularx-social-login";
import { UserService } from "src/app/services/user/user.service";
import { User } from "src/app/models/user-model";
import { ProfileService } from "src/app/services/profile/profile.service";

@Component({
  selector: "app-register",
  templateUrl: "./register.component.html",
  styleUrls: ["./register.component.scss"],
})
export class RegisterComponent implements OnInit, AfterViewInit {
  return_url: string = "";
  step: string = "mode";
  provider: string = "email";
  user_id: string = "";
  token: string = "";
  email: string = "";
  username: string = "";
  form!: FormGroup;
  submitted = false;
  phoneInputElement!: any;

  constructor(
    private authService: AuthService,
    private router: Router,
    private formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private profileService: ProfileService,
    private socialauthenticationService: SocialAuthService
  ) {}

  //validator submit
  get formControl() {
    return this.form.controls;
  }

  ngOnInit() {
    this.initPhone("FR");
    this.activatedRoute.queryParams.subscribe((params) => {
      this.updateQueryParams(params, [
        "step",
        "provider",
        "user_id",
        "token",
        "email",
        "username",
      ]);
      this.form = this.formBuilder.group({
        role: ["artist", Validators.required],
        title: ["monsieur", Validators.required],
        email: [
          params.email ? params.email : "",
          this.provider != "email"
            ? []
            : [Validators.required, Validators.email],
        ],
        full_name: ["", Validators.required],
        cgu: [false],
        charte: [false],
        password: [
          "",
          this.provider != "email"
            ? []
            : [Validators.required, Validators.minLength(6)],
        ],
        confirmPassword: [
          "",
          this.provider != "email"
            ? []
            : [Validators.required, Validators.minLength(6)],
        ],
        phone: [
          "",
          Validators.required,
          Validators.minLength(9),
          Validators.maxLength(9),
        ],
        country_phone: [""],
        country_code_phone: [""],
      });
    });
  }

  ngAfterViewInit(): void {
    Webflow.setIFrameHeight();
    this.initPhone("FR");
  }

  initPhone(countryCode: string) {
    const inputElement = document.getElementById("phone");
    if (inputElement) {
      this.phoneInputElement = intlTelInput(inputElement as HTMLInputElement, {
        initialCountry: countryCode,
        separateDialCode: true,
      });
    }
  }

  checkPasswords(group: FormGroup) {
    // here we have the 'passwords' group
    const password = group.get("password")?.value;
    const confirmPassword = group.get("confirmPassword")?.value;
    return password === confirmPassword ? null : { notSame: true };
  }

  onSubmit() {
    this.submitted = true;
    if (this.form.invalid) {
      let message = "";
      if (this.form.controls["password"].errors?.required) {
        message = 'Le champ "Mot de passe" est requis';
      } else if (this.form.controls["password"].errors?.minlength) {
        message = "Le mot de passe doit contenir au moins 6 caractères";
      } else if (this.form.controls["confirmPassword"].errors?.required) {
        message = 'Le champ "Confirmez votre mot de passe" est requis';
      } else if (this.form.value.password !== this.form.value.confirmPassword) {
        message = "Les mots de passe ne correspondent pas";
      } else {
        message = "Veuillez vérifier vos informations";
      }
      Webflow.setIFrameHeight();
      Swal.fire({
        title: "Érreur",
        html:
          "<p>" +
          message +
          "</p>" +
          (this.form.get("email")?.invalid && this.form.get("email")?.touched
            ? "<p>Le format de l'adresse email n'est pas valide</p>"
            : ""),
        icon: "error",
        position: "top",
      });

      return;
    }
    if (!this.form.value.cgu) {
      Webflow.setIFrameHeight();
      Swal.fire({
        title: "Attention",
        text: "Veuillez accepter les conditions d'utilisation du site",
        icon: "info",
        position: "top",
      });
      return;
    }
    if (!this.form.value.charte) {
      Webflow.setIFrameHeight();
      Swal.fire({
        title: "Attention",
        text: "Veuillez accepter la charte STUDIOMAP et du studio",
        icon: "info",
        position: "top",
      });
      return;
    }

    let ob: any = this.checkPasswords(this.form);
    if (ob && ob["notSame"] == true) {
      Webflow.setIFrameHeight();
      Swal.fire({
        title: "Erreur",
        text: "les mots de passe ne sont pas identiques",
        icon: "error",
        position: "top",
      });
      return;
    }

    let user: any = {};
    // send phone data
    const countryData = this.phoneInputElement.getSelectedCountryData();

    user.email = this.form.value.email;
    user.password = this.form.value.password;
    user.username = this.form.value.email;
    user.full_name = this.form.value.full_name;
    user.title = this.form.value.title;
    user.role = this.form.value.role;
    user.country_phone = countryData.iso2;
    user.phone = this.form.value.phone.replace(/\s+/g, "");
    user.country_code_phone = "+" + countryData.dialCode;

    if (this.form.value.phone.replace(/\s+/g, "").length != 9) {
      Swal.fire({
        title: "Attention",
        text: "Veuillez vérifier votre numéro de téléphone",
        icon: "info",
        position: "top",
      });
      return;
    }

    this.spinner.show();
    // fill user object
    if (this.provider == "email") {
      this.authService.register(user).subscribe(
        (res) => {
          Webflow.setIFrameHeight();
          Swal.fire({
            title: "Félicitation",
            text: "Votre inscription a été validée. Pour pouvoir vous connecter, nous vous invitons à cliquer sur le lien que vous avez reçu sur votre adresse email pour valider votre compte.",
            icon: "success",
            position: "top",
          });
          this.form.reset();
          this.form.patchValue({ title: "monsieur" });
          this.form.patchValue({ role: "artist" });
          this.submitted = false;
          this.spinner.hide();

          Webflow.myAction(this.router, "login", {});
        },
        (error) => {
          this.spinner.hide();
          Webflow.setIFrameHeight();
          if (GenerateErrors.check_if_is_custom_error(error)) {
            Swal.fire({
              title: "Érreur",
              text: GenerateErrors.get_message_error(error),
              icon: "error",
              position: "top",
            });
          } else if (
            error.error.error.message.includes(
              "Email or Username are already taken"
            )
          ) {
            if (
              error.error.error.message == "Email or Username are already taken"
            ) {
              Swal.fire({
                title: "Érreur",
                text: "L'adresse email est déjà utilisée",
                icon: "error",
                position: "top",
              });
            } else {
              try {
                const parsedObject = JSON.parse(error.error.error.message);
                if (parsedObject.provider == "local") {
                  Swal.fire({
                    title: "Érreur",
                    text: "Un compte a déjà été créé avec cette adresse e-mail en utilisant Mot de passe",
                    icon: "error",
                    position: "top",
                  });
                } else {
                  Swal.fire({
                    title: "Érreur",
                    text:
                      "Un compte a déjà été créé avec cette adresse e-mail en utilisant" +
                      parsedObject.provider,
                    icon: "error",
                    position: "top",
                  });
                }
              } catch (error) {
                Swal.fire({
                  title: "Érreur",
                  text: "L'adresse email est déjà utilisée",
                  icon: "error",
                  position: "top",
                });
              }
            }
          } else {
            Swal.fire({
              title: "Erreur",
              text: "Erreur de connexion",
              icon: "error",
              position: "top",
            });
          }
        }
      );
    } else if (this.provider == "google" || this.provider == "facebook") {
      user.user = this.user_id;
      this.create_profile(user, this.token);
    } else {
      this.spinner.hide();
      Swal.fire({
        title: "Erreur",
        text: "Erreur de connexion",
        icon: "error",
        position: "top",
      });
    }
  }

  updateQueryParams(params: any, keys: string[]) {
    keys.forEach((key) => {
      this[key] = params[key] ?? this[key];
    });
  }

  navigateTo(actionName: string) {
    Webflow.myAction(this.router, actionName, {});
  }

  to_form(provider: string) {
    this.provider = provider;
    if (provider == "email") {
      this.step = "form";
      this.generate_params();
    } else if (provider == "google") {
      this.registerWithGoogle();
    } else if (provider == "facebook") {
      this.registerWithFB();
    } else if (provider == "phone") {
      this.registerWithPhone();
      /*
      this.provider = "phone";
      this.step = "form";
      this.generate_params();
      */
    } else {
      this.provider = "email";
      this.step = "form";
      this.generate_params();
    }
  }

  generate_params() {
    let params: any = {};
    params.step = this.step;
    params.provider = this.provider;
    params.user_id = this.user_id;
    params.email = this.email;
    params.username = this.username;
    params.token = this.token;
    if (this.return_url != "") {
      params.return = this.return_url;
    }
    Webflow.myAction(this.router, "register", params);
  }

  registerWithFB() {
    Webflow.setIFrameHeight();
    Swal.fire({
      title: "en construction",
      text: "Bientôt disponible",
      icon: "info",
      position: "top",
    });
    /*
    this.socialauthenticationService
      .signIn(FacebookLoginProvider.PROVIDER_ID)
      .then((user) => {
        this.checkIfAccountExist("facebook", user);
      })
      .catch((err) => {
        Webflow.setIFrameHeight();
        Swal.fire({
          title: "Error",
          text: "Une érreur s'est produite veuillez réessayer",
          icon: "error",
          position: "top",
        });
      });
      */
  }

  registerWithGoogle() {
    this.socialauthenticationService
      .signIn(GoogleLoginProvider.PROVIDER_ID, {
        scope: "profile email",
      })
      .then((user) => {
        this.checkIfAccountExist("google", user);
      })
      .catch((err) => {
        Webflow.setIFrameHeight();
        Swal.fire({
          title: "Error",
          text: "Une érreur s'est produite veuillez réessayer",
          icon: "error",
          position: "top",
        });
      });
  }

  registerWithPhone() {
    Webflow.setIFrameHeight();
    Swal.fire({
      title: "en construction",
      text: "Bientôt disponible",
      icon: "info",
      position: "top",
    });
  }

  checkIfAccountExist(provider: string, user: any) {
    this.spinner.show();
    this.authService
      .loginWith(provider, "&access_token=" + user.authToken)
      .subscribe(
        (data: any) => {
          this.spinner.show();
          this.userService
            .get(data.jwt)
            .subscribe(
              (user) => {
                let userJson = User.toJson(
                  user,
                  data.jwt,
                  false,
                  user.role.type
                );
                if (userJson != null) {
                  Webflow.setIFrameHeight();
                  Swal.fire({
                    title: "Erreur",
                    text: "L'adresse email est déjà utilisée",
                    icon: "error",
                    position: "top",
                  });
                  this.spinner.hide();
                } else {
                  this.step = "form";
                  this.token = data.jwt;
                  this.user_id = user.id;
                  this.username = user.username;
                  this.email = user.email;
                  this.generate_params();
                }
              },
              (err) => {
                Webflow.setIFrameHeight();
                Swal.fire({
                  title: "Erreur",
                  text: "Impossible de s'inscrire avec cette adresse email. \n veuillez contacter le support à support@studiomap.fr .",
                  icon: "error",
                  position: "top",
                });
              }
            )
            .add(() => {
              this.spinner.hide();
            });
        },
        (err) => {
          Webflow.setIFrameHeight();
          Swal.fire({
            title: "Erreur",
            text: "Une érreur s'est produite veuillez réessayer",
            icon: "error",
            position: "top",
          });
        }
      )
      .add(() => {
        this.spinner.hide();
      });
  }

  back() {
    let params: any = {};
    if (this.return_url != "") {
      params.return = this.return_url;
    }
    Webflow.myAction(this.router, "register", params);
  }

  create_profile(profile: any, jwt: string) {
    this.profileService
      .create(profile, jwt)
      .subscribe((profileAdded) => {
        this.userService.get(jwt).subscribe((user) => {
          let userJson = User.toJson(user, jwt, false, user.role.type);
          if (userJson != null) {
            Webflow.setIFrameHeight();
            Swal.fire({
              title: "Félicitation",
              text: "Votre inscription a été validée.",
              icon: "success",
              position: "top",
            });
            localStorage.setItem("studiomapUser", JSON.stringify(userJson));
            this.authService.callCurrentUserSubject(userJson);

            if (this.return_url != "") {
              Webflow.myAction(this.router, "", { return: this.return_url });
            } else {
              Webflow.myAction(this.router, "", {});
            }
          } else {
            Webflow.setIFrameHeight();
            Swal.fire({
              title: "Error",
              text: "Impossible de se connecter avec se compte. \n veuillez contacter le support à support@studiomap.fr .",
              icon: "error",
              position: "top",
            });
          }
        });
      })
      .add(() => {
        this.spinner.hide();
      });
  }
}
